/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as _ from 'lodash';

import HealthEntryCategoryNames from '../../../../constants/HealthEntryCategoryNames';
import MedliAmountText from '../../../components/MedliAmountText';
import GeneVariantResultText from '../../../components/GeneVariantResultText';
import GeneAttributesListResultText, { areGeneAttributesDefined } from '../../../components/GeneAttributesListResultText';
import { HealthEntryDateSummaryDate } from '../../../localization/LocalizedDate';
import { createGeneVariantResult } from '../../../../models/HealthEntryCompose/geneVariantResult';
import { LocalizedTime } from '../../../localization/LocalizedTime';

interface ValueProps {
  heValue: any;
  className: string;
}

class AmountValue extends React.Component<ValueProps, {}> {

  render() {
    const { heValue, className } = this.props;

    const hasAmount = heValue.amount && heValue.amount.value;
    const hasDescription = heValue.description && heValue.description.id;

    if (hasAmount || hasDescription) {
      return (
        <MedliAmountText description={heValue.description} amount={heValue.amount} className={className} />
      );
    } else {
      return (
        <span className={className}>
          <FormattedMessage id="ui-data.value.not-specified.text" />
        </span>
      );
    }
  }
}

class GeneVariantPresent extends React.Component<ValueProps, {}> {

  render() {
    const { heValue, className } = this.props;
    const isDefined = !!_.get(heValue, 'gene_variant_result.gene_variant_present_id', null);

    if (isDefined) {
      return (
        <GeneVariantResultText geneVariantResult={heValue.gene_variant_result} className={className} />
      );
    } else {
      return (
        <span className={className}>
          <FormattedMessage id="ui-data.value.not-specified.text" />
        </span>
      );
    }
  }
}

const Value = (props) => {
  switch (props.category) {
    case HealthEntryCategoryNames.geneVariantResult:
    case HealthEntryCategoryNames.geneticResult:
      return <GeneVariantPresent {...props} />;
    case HealthEntryCategoryNames.lab:
    case HealthEntryCategoryNames.measurement:
      return <AmountValue {...props} />;
    default:
      return null;
  }
};

const AdditionalData = (props) => {
  switch (props.category) {
    case HealthEntryCategoryNames.geneVariantResult:
    case HealthEntryCategoryNames.geneticResult:
      const { heValue } = props;
      const geneVariantResult = createGeneVariantResult(heValue.gene_variant_result);

      if (!areGeneAttributesDefined(geneVariantResult)) return null;

      return (
        <GeneAttributesListResultText
          style={{ marginTop: 8 }}
          geneVariantResult={geneVariantResult}
        />
      );
    default:
      return null;
  }
};

const Date = (props) => {
  const { date, className } = props;

  if (!!date) {
    return (
      <span className={className}>
        <HealthEntryDateSummaryDate
          startDateData={date}
          endDateData={null}
          isCurrent={false}
          isPeriod={false}
        />
      </span>
    );
  } else {
    return (
      <span className={className}>
        <FormattedMessage id="ui-data.date.not-specified.text" />
      </span>
    );
  }
};

const HEValueDate = (props) => {
  const { heValue, className } = props;
  return <Date date={heValue.date} className={className} />;
};

interface ObservationProps {
  observation: any;
}
const Observation: React.FC<ObservationProps> = (props) => {
  const { observation } = props;

  const attributeCount = observation.attributes.length;
  if (attributeCount === 0) {
    return <FormattedMessage id="ui-data.attributes.not-specified.text" />;
  }
  const attCount = observation.attributes.length - 1;

  return (
    <>
      {
        observation.attributes.map((a, i) => {
          const isLast = i === attCount;
          return (
            <span key={i}>
              <span>{a.name}</span>
              {
                a.summary ? <>: <span style={{ fontWeight: 'bold' }}>{a.summary}</span>{isLast ? null : ', '}</> : null
              }
            </span>
          );
        })
      }
    </>
  );
};

const ObservationDate = (props) => {
  const { observation } = props;
  return <Date date={observation.date} className="" />;
};

const ObservationTime = (props) => {
  const { observation } = props;

  return (
    <span className="">
      <LocalizedTime dateTime={observation.date} />
    </span>
  );
};
const HEValueFormatter = {
  Value,
  AdditionalData,
  HEValueDate,
  Observation,
  ObservationDate,
  ObservationTime,
};

export default HEValueFormatter;
